'use client'
import { PrismicLink, PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import '@splidejs/react-splide/css/core';
import CtaSimple from "@/components/CtaSimple";


/**
 * @typedef {import("@prismicio/client").Content.HowItWorksStepsSlice} HowItWorksStepsSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<HowItWorksStepsSlice>} HowItWorksStepsProps
 * @type {import("react").FC<HowItWorksStepsProps>}
 */

const StepMobile = ({ slice }) => {
  const { title, steps, app_links, video, poster } = slice.primary;
  return (
    <div className="max-w-240 mx-auto flex flex-col gap-10 md:gap-20">
      <div className="text-center">
        {isFilled.richText(title) && (
          <PrismicRichText
            field={title}
            components={{
              heading2: ({ children }) => (
                <h2 className="h2-sm">{children}</h2>
              ),
              heading3: ({ children }) => (
                <h3 className="h2-sm">{children}</h3>
              ),
              heading4: ({ children }) => (
                <h4 className="h2-sm">{children}</h4>
              ),
            }}
          />
        )}
      </div>
      <div className="flex flex-col lg:flex-row gap-20 xl:gap-40">
        <div className="max-w-[380px] w-full mx-auto relative">
          <div className="video-mask p-3.5">
            <video
              autoPlay
              muted
              playsInline
              loop
              poster={poster.url}
              title={title?.[0]?.text || "Vidéo de démonstration"}
              width="360"
              height="780"
              aria-label={title?.[0]?.text || "Vidéo de démonstration"}
            >
              <source src={`${video?.url}`} type="video/mp4" />
            </video>
          </div>
          <img
            src="/mockup-iphone.svg"
            alt="Mockup iPhone"
            className="h-full w-full absolute top-0 pointer-events-none"
            loading="lazy"
            width="360"
            height="780"
          />
        </div>
        <div className="flex flex-col justify-center gap-10">
          {isFilled.group(steps) && (
            <div className="flex flex-col gap-10">
              {steps.map((item, idx) => (
                <div key={idx} className="flex flex-col gap-1">
                  <div className="flex gap-4 items-center">
                    {isFilled.number(item.number) && (
                      <div className="bg-yellow rounded-full aspect-square h-[46px] w-[46px] flex items-center justify-center text-dark-green">
                        <span className="text-number pb-1.5">{item.number}</span>
                      </div>
                    )}
                    {isFilled.richText(item.title) && (
                      <PrismicRichText
                        field={item.title}
                        components={{
                          heading3: ({ children }) => (
                            <h3 className="h3-sm uppercase">{children}</h3>
                          ),
                          heading4: ({ children }) => (
                            <h4 className="h3-sm uppercase">{children}</h4>
                          ),
                          heading5: ({ children }) => (
                            <h5 className="h3-sm uppercase">{children}</h5>
                          ),
                        }}
                      />
                    )}
                  </div>
                  <div>
                    {isFilled.richText(item.description) && (
                      <PrismicRichText
                        field={item.description}
                        components={{
                          heading3: ({ children }) => (
                            <h3 className="h3-sm uppercase">{children}</h3>
                          ),
                          heading4: ({ children }) => (
                            <h4 className="h3-sm uppercase">{children}</h4>
                          ),
                          heading5: ({ children }) => (
                            <h5 className="h3-sm uppercase">{children}</h5>
                          ),
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {Array.isArray(app_links) && app_links.length > 0 && (
            <div>
              <div className="flex md:flex-wrap gap-4">
                {app_links.map((link, idx) =>
                  isFilled.link(link) ? (
                    <div key={idx} className="w-1/2 md:w-auto">
                      {link.variant === "google_play" ? (
                        <PrismicLink field={link}>
                          <img
                            src="/google-play.svg"
                            alt="Disponible sur Google Play"
                            className="h-auto md:h-12 w-full"
                            loading="lazy"
                            width="150"
                            height="50"
                          />
                        </PrismicLink>
                      ) : (
                        <PrismicLink field={link}>
                          <img
                            src="/app-store.svg"
                            alt="Télécharger dans l'App Store"
                            className="h-auto md:h-12 w-full"
                            loading="lazy"
                            width="150"
                            height="50"
                          />
                        </PrismicLink>
                      )}
                    </div>
                  ) : null
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StepDesktop = ({ slice }) => {
  const { title, steps, app_links, video, poster, cta } = slice.primary;
  const arrowsHidden = steps.length <= 3 ? 'arrows-hidden' : '';

  return (
    <div className="flex flex-col gap-10 md:gap-20">
      <div className="text-center">
        {isFilled.richText(title) && (
          <PrismicRichText
            field={title}
            components={{
              heading2: ({ children }) => (
                <h2 className="h2-sm">{children}</h2>
              ),
              heading3: ({ children }) => (
                <h3 className="h2-sm">{children}</h3>
              ),
              heading4: ({ children }) => (
                <h4 className="h2-sm">{children}</h4>
              ),
            }}
          />
        )}
      </div>
      <div className="flex flex-col gap-20">
        <div className="max-w-[800px] w-full mx-auto relative">
          <video autoPlay muted playsInline loop poster={poster.url} className="aspect-[527/329] w-full h-full object-cover pt-4 pb-7 px-11 md:pt-10 md:pb-15 md:px-21">
            <source src={`${video?.url}`} type="video/mp4" />
          </video>
          <img
            src="/mockup-mac.svg"
            alt="Mockup Mac"
            className="h-full w-full absolute top-0 pointer-events-none"
            loading="lazy"
            width="800"
            height="500"
          />
        </div>
        <div className={`flex flex-col justify-center gap-10 ${slice.primary.bg_color === 'dark-green' ? 'arrows-white' : ''} ${arrowsHidden}`}>
          {isFilled.group(steps) && (
            <div>
              <div className="hidden lg:block">
                <Splide
                  options={{
                    type: 'slide',
                    perPage: 3,
                    gap: '5rem',
                    pagination: false,
                    arrows: true,
                    breakpoints: {
                      1024: { perPage: 2 },
                    },
                  }}
                  className="slide-steps"
                >
                  {steps.map((item, idx) => (
                    <SplideSlide key={idx}>
                      <div className="flex flex-col gap-5 h-full">
                        <div className="flex gap-4 items-center min-h-[56px]">
                          {isFilled.number(item.number) && (
                            <div className="bg-yellow rounded-full aspect-square h-[46px] w-[46px] flex items-center justify-center text-dark-green">
                              <span className="text-number pb-1.5">{item.number}</span>
                            </div>
                          )}
                          {isFilled.richText(item.title) && (
                            <PrismicRichText
                              field={item.title}
                              components={{
                                heading3: ({ children }) => (
                                  <h3 className="h3-sm uppercase">{children}</h3>
                                ),
                                heading4: ({ children }) => (
                                  <h4 className="h3-sm uppercase">{children}</h4>
                                ),
                                heading5: ({ children }) => (
                                  <h5 className="h3-sm uppercase">{children}</h5>
                                ),
                              }}
                            />
                          )}
                        </div>
                        <div>
                          {isFilled.richText(item.description) && (
                            <PrismicRichText
                              field={item.description}
                              components={{
                                heading3: ({ children }) => (
                                  <h3 className="h3-sm uppercase">{children}</h3>
                                ),
                                heading4: ({ children }) => (
                                  <h4 className="h3-sm uppercase">{children}</h4>
                                ),
                                heading5: ({ children }) => (
                                  <h5 className="h3-sm uppercase">{children}</h5>
                                ),
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </SplideSlide>
                  ))}
                </Splide>
              </div>
              <div className="flex flex-col gap-10 lg:hidden">
                {steps.map((item, idx) => (
                  <div key={idx} className="flex flex-col gap-1">
                    <div className="flex gap-4 items-center">
                      {isFilled.number(item.number) && (
                        <div className="bg-yellow rounded-full aspect-square h-[46px] w-[46px] flex items-center justify-center text-dark-green">
                          <span className="text-number pb-1.5">{item.number}</span>
                        </div>
                      )}
                      {isFilled.richText(item.title) && (
                        <PrismicRichText
                          field={item.title}
                          components={{
                            heading3: ({ children }) => (
                              <h3 className="h3-sm uppercase">{children}</h3>
                            ),
                            heading4: ({ children }) => (
                              <h4 className="h3-sm uppercase">{children}</h4>
                            ),
                            heading5: ({ children }) => (
                              <h5 className="h3-sm uppercase">{children}</h5>
                            ),
                          }}
                        />
                      )}
                    </div>
                    <div>
                      {isFilled.richText(item.description) && (
                        <PrismicRichText
                          field={item.description}
                          components={{
                            heading3: ({ children }) => (
                              <h3 className="h3-sm uppercase">{children}</h3>
                            ),
                            heading4: ({ children }) => (
                              <h4 className="h3-sm uppercase">{children}</h4>
                            ),
                            heading5: ({ children }) => (
                              <h5 className="h3-sm uppercase">{children}</h5>
                            ),
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(cta) && cta.length > 0 && (
            <>
              <div className="flex flex-wrap gap-5 justify-center mt-10">
                {/* Variants classiques */}
                {cta.find((link) =>
                 link.variant === "yellow") && (
                    <CtaSimple page={cta.find((link) => link.variant === "yellow")} padding={"px-10"} width={"w-full md:w-fit"} hover={"white"} />
                  )}
                {/* Google play */}
                {cta.find((link) => link.variant === "google_play") && (
                  <PrismicLink field={cta.find((link) => link.variant === "google_play")}>
                    <img
                      src="/google-play.svg"
                      alt="Disponible sur Google Play"
                      className="h-auto md:h-12 w-full"
                    />
                  </PrismicLink>
                )}

                {/* App Store */}
                {cta.find((link) => link.variant === "app_store") && (
                  <PrismicLink field={cta.find((link) => link.variant === "app_store")}>
                    <img
                      src="/app-store.svg"
                      alt="Télécharger dans l'App Store"
                      className="h-auto md:h-12 w-full"
                    />
                  </PrismicLink>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const HowItWorksSteps = ({ slice }) => {
  const { bg_color } = slice.primary;
  return (
    <section
      id={slice.slice_type}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`relative ${bg_color == 'dark-green' ? 'bg-dark-green text-white py-20 md:py-30' : 'bg-white text-dark-green my-14 md:my-30'} framing overflow-hidden`}
    >
      {slice.variation === "desktop" ? (
        <StepDesktop slice={slice} />
      ) : (
        <StepMobile slice={slice} />
      )}
    </section>
  );
};

export default HowItWorksSteps;
